import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { recipientsFetch } from '@/lib/recipients/api';
import { useWorkspaceScopedEnabled } from '@/lib/api/use-workspace-scoped-enabled';
import { usePermissionGate } from '@/components/auth/permission-gate';
import { customFieldKeys, customFieldPresetKeys } from '@/hooks/api/query-keys';
import type { CustomField } from '@/lib/types/recipients';

export interface RecipientCustomFieldPreset {
  id: string;
  scope: 'Global' | 'Company' | 'Workspace';
  companyId: string | null;
  workspaceId: string | null;
  parentId: string | null;
  key: string;
  label: string;
  type: string;
  isRequired: boolean;
  description?: string | null;
  validation?: Record<string, unknown> | null;
  /**
   * Select / Multi Select choices — the stored values (migration 0204).
   * Empty = any value. Always an array here (normalised in `select`).
   */
  options: string[];
  canEdit: boolean;       // server-computed (Workspace-tier only)
  willClone: boolean;     // server-computed
  createdAt: string;
  updatedAt: string;
}

export interface ListPresetsResponse {
  presets: RecipientCustomFieldPreset[];
}

/** An API that predates `options` sends none: read it as "any value". */
function normalizePresets(raw: ListPresetsResponse | null | undefined): ListPresetsResponse {
  return {
    presets: (raw?.presets ?? []).map((preset) => ({
      ...preset,
      options: Array.isArray(preset.options) ? preset.options : [],
    })),
  };
}

export function useRecipientCustomFieldPresets() {
  // Presets are workspace-scoped. In production: gate on a concrete
  // workspace selection. In demo: always enabled (demo-bootstrap always
  // binds a workspace). See lib/api/use-workspace-scoped-enabled.ts.
  const enabled = useWorkspaceScopedEnabled();
  // The role (browser QA, 2026-10-06): the read is
  // read:RecipientCustomFieldPreset (custom-field-presets.controller.ts), which
  // a role that manages recipients need not hold — the helpdesk role met two
  // 403s on Add Recipient, Data Health and Custom Fields. Asked only once the
  // grants are KNOWN to include it; every caller reads "none" as an empty list.
  const gate = usePermissionGate('read', 'RecipientCustomFieldPreset');
  return useQuery({
    queryKey: customFieldPresetKeys.list(),
    queryFn: () => recipientsFetch<ListPresetsResponse>('custom-field-presets'),
    select: normalizePresets,
    enabled: enabled && !gate.loading && !gate.denied,
    staleTime: 5 * 60 * 1000, // 5 minutes — presets change rarely
  });
}

const NO_PRESETS: RecipientCustomFieldPreset[] = [];

/** The presets this workspace can see, as a list (empty while loading). */
export function useRecipientCustomFieldPresetList(): RecipientCustomFieldPreset[] {
  const { data } = useRecipientCustomFieldPresets();
  return data?.presets ?? NO_PRESETS;
}

/**
 * Copy a Global or Company preset into this workspace (POST with `parentId`):
 * the copy carries the parent's choices and, unlike the parent, can be edited.
 */
export function useCustomiseRecipientCustomFieldPreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (parentId: string) =>
      recipientsFetch<RecipientCustomFieldPreset>('custom-field-presets', {
        method: 'POST',
        body: JSON.stringify({ parentId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFieldPresetKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

/** Replace a Workspace preset's choices. */
export function useUpdateRecipientCustomFieldPresetOptions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, options }: { id: string; options: string[] }) =>
      recipientsFetch<RecipientCustomFieldPreset>(`custom-field-presets/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ options }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFieldPresetKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

/**
 * Make a preset one of this workspace's own fields (POST custom-fields with
 * `presetId`). The API copies the preset's key, type and choices, so values
 * already written under the key keep their meaning.
 */
export function useAdoptRecipientCustomFieldPreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ preset, name }: { preset: RecipientCustomFieldPreset; name: string }) =>
      recipientsFetch<CustomField>('custom-fields', {
        method: 'POST',
        body: JSON.stringify({ presetId: preset.id, name, type: preset.type }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
